import Loading from "../../components/general/Loading";
import { useState , useEffect} from "react";
import ImportEvolution from "./ImportEvolution";
import DeleteAnteriorEvalNat from "./DeleteAnteriorEvalNat";
import { useEvalNat } from "../../hooks/fetch_api_hook/useEvalNat";
import { useMemo } from "react";
import { getClassesWithoutNationalEvaluations } from "../../utils/evalnatfunction";
import { niveauDe } from "../../utils/campagneEvalNat";
import ExportEvolution from "./ExportEvolution";
import { useUser } from "../../context/UserContext";
import { TrendingUp, AlertTriangle, ChevronDown, Calculator, BookOpen, ArrowUp, ArrowDown, Minus, History } from "lucide-react";

const Evolution = () => {
  const [testSpecifique, setTestSpecifique] = useState("Automatismes");
  const [niveau, setNiveau] = useState("6eme"); // Niveau sélectionné
  const [selectedMatiere, setSelectedMatiere] = useState('maths'); // Par défaut: maths
  const [referenceYear, setReferenceYear] = useState(2025);
  const [comparisonYear, setComparisonYear] = useState();
  const [data, setData] = useState([]);
  const [reload, setReload] = useState(false)
  const [showImportModal, setShowImportModal] = useState(false);
  const { etablissementData} = useUser();
       
   const { data: EvalsNatGroupe, isLoading: isLoadingEvalNat } = useEvalNat(
    etablissementData?.id ? `?etablissement_id=${parseInt(etablissementData.id, 10)}` : null,
      null,
      reload,
  );

  const handleReload = () => {
    setReload(!reload);
    setData([])
  }

  const isClasseWitouthResult = useMemo( () => {
    // Si EvalsNatGroupe est vide, renvoyer toutes les classes du niveau
      if (!EvalsNatGroupe || EvalsNatGroupe.length === 0) {
        // Vérifier que etablissementData existe et a des classes
        if (!etablissementData?.classes) {
          return [];
        }
        // Retourner toutes les classes qui commencent par le niveau
        return etablissementData.classes
          .filter(classe => classe.nom?.startsWith(niveau[0]))
          .map(classe => classe.nom);
      }
    return getClassesWithoutNationalEvaluations(
      etablissementData,
      EvalsNatGroupe.filter((evalNat) =>
        niveauDe(evalNat) === niveau[0] // Par exemple, niveau = "6" ; une campagne porte son niveau (D9)
      ),
      niveau[0],
      selectedMatiere // Filtrer par matière
    )
  },[EvalsNatGroupe, etablissementData, niveau, selectedMatiere])

  const filteredEvalsNatAnteriorSession = useMemo(() => {
    return (
      EvalsNatGroupe?.filter((evalNat) =>
        evalNat.classe_nom === "Non défini" &&
        evalNat.stats?.niveau === niveau &&
        evalNat.matiere === selectedMatiere
      ) || []
    );
  }, [EvalsNatGroupe, niveau, selectedMatiere]);

  const mergeEvalsNatReferenceSession = useMemo(() => {
    if (EvalsNatGroupe.length ===0) {
      return {};
    }
    // Filtrer les évaluations du niveau spécifié (évaluation par classe ou campagne, D9) et par matière
    const filteredEvals = EvalsNatGroupe.filter((evalNat) =>
      niveauDe(evalNat) === niveau[0] &&
      evalNat.titre?.toUpperCase().includes("EVALUATION NATIONALE") &&
      evalNat.matiere === selectedMatiere
    );
    // Fusionner les réponses et les questions
    return {
      classe_nom: `Tous les élèves ${niveau}`,
      eleves_reponses: filteredEvals.flatMap((evalNat) => evalNat.eleves_reponses || []),
      questions: filteredEvals[0]?.questions || null, // Utilisation de l'optional chaining pour plus de sécurité
    };
  }, [EvalsNatGroupe, niveau, selectedMatiere]);


  const stats = useMemo(() => {
    if (
      !mergeEvalsNatReferenceSession ||
      !Array.isArray(mergeEvalsNatReferenceSession.eleves_reponses) ||
      !Array.isArray(mergeEvalsNatReferenceSession.questions) ||
      mergeEvalsNatReferenceSession.eleves_reponses.length === 0 ||
      mergeEvalsNatReferenceSession.questions.length === 0
    ) {
      return {};
    }
    // Fonction pour mapper type_label vers la clé de stats (pour regrouper)
    const getTypeKey = (typeLabel) => {
      if (!typeLabel) return 'Autre';

      // Maths
      if (typeLabel === "Automatisme") return "Automatismes";
      if (typeLabel === "Résolution de problèmes") return "Problèmes";

      // Français
      if (typeLabel.startsWith("Compréhension de l'écrit")) return "ComprehensionEcrite";
      if (typeLabel === "Lexique") return "Lexique";
      if (typeLabel === "Compétences langagières et linguistiques") return "CompetencesLangagieres";

      return 'Autre';
    };

    // Fonction pour obtenir la clé d'accès aux réponses des élèves
    const getResponseKey = (typeLabel) => {
      if (!typeLabel) return null;

      // Maths - les clés sont directes
      if (typeLabel === "Automatisme") return "Automatismes";
      if (typeLabel === "Résolution de problèmes") return "Problèmes";

      // Français - Lexique est direct
      if (typeLabel === "Lexique") return "Lexique";

      // Français - Compréhension écrite : extraire le nom du texte après "Compréhension de l'écrit "
      // Ex: "Compréhension de l'écrit Arachné" -> "Arachné"
      // Ex: "Compréhension de l'écrit Document Sommeil" -> "Sommeil"
      // Ex: "Compréhension de l'écrit Petit Chose" -> "Petit Chose"
      if (typeLabel.startsWith("Compréhension de l'écrit ")) {
        let textName = typeLabel.replace("Compréhension de l'écrit ", "").trim();
        // Retirer "Document " si présent (ex: "Document Sommeil" -> "Sommeil")
        if (textName.startsWith("Document ")) {
          textName = textName.replace("Document ", "").trim();
        }
        return textName;
      }

      // Français 5ème - Compétences langagières -> les réponses sont dans "Compétence"
      if (typeLabel === "Compétences langagières et linguistiques") return "Compétence";

      return null;
    };

    // Étape 1 : Calculer le pourcentage de réussite par question
    const calculateSuccessRates = (questions, elevesReponses) => {
      return questions.reduce((acc, question) => {
        const questionId = question.id;
        const totalEleves = elevesReponses.length;
        const typeQuestion = getTypeKey(question.type_label);
        const responseKey = getResponseKey(question.type_label);

        const correctAnswersCount = elevesReponses.filter(
          (eleve) => {
            // Vérifications défensives pour éviter les erreurs "Cannot read properties of undefined"
            if (!eleve || !eleve.reponses) return false;
            if (!responseKey) return false;
            if (!eleve.reponses[responseKey]) return false;
            if (!eleve.reponses[responseKey][question.question_num]) return false;

            return eleve.reponses[responseKey][question.question_num].score === 1;
          }
        ).length;
        // Calculer le pourcentage de réussite
        const successRate = totalEleves > 0 ? correctAnswersCount / totalEleves : 0;
        // Ajouter les données au résultat
        acc[questionId] = {
          successRate: parseFloat(successRate.toFixed(2)),
          type: typeQuestion,
        };
        return acc;
      }, {});
    };
    // Étape 2 : Organiser les statistiques par type
    const groupByType = (statsByQuestion) => {
      return Object.entries(statsByQuestion).reduce((acc, [questionId, { successRate, type }]) => {
        // Initialiser un tableau pour ce type si nécessaire
        if (!acc[type]) {
          acc[type] = [];
        }

        // Ajouter les stats pour cette question
        acc[type].push({ [questionId]: successRate });

        return acc;
      }, {});
    };

    // Calculer les statistiques
    const statsByQuestion = 
    calculateSuccessRates(
      mergeEvalsNatReferenceSession.questions,
      mergeEvalsNatReferenceSession.eleves_reponses
    );
    return groupByType(statsByQuestion);
  }, [mergeEvalsNatReferenceSession]);


const filteredEvalsNatReferenceSession = useMemo( ()=> {
 if(EvalsNatGroupe && EvalsNatGroupe.length > 0) {
  return {
    session : 2025,
    questions : EvalsNatGroupe.find((evaluation) =>
      niveauDe(evaluation) === niveau[0] &&
      evaluation.matiere === selectedMatiere
    )?.questions || null,
    stats : { niveau : niveau ,
      ...stats
    }
  }
}
else{
  return {}
}
},[EvalsNatGroupe,stats,niveau,selectedMatiere])


const mergedSession = useMemo(() => {
  // Vérifiez si les deux variables existent et initialisez un tableau vide si nécessaire
  const anteriorSessions = Array.isArray(filteredEvalsNatAnteriorSession)
    ? filteredEvalsNatAnteriorSession
    : [];
  // Retourne un nouveau tableau combiné
  return [...anteriorSessions, filteredEvalsNatReferenceSession];
}, [filteredEvalsNatAnteriorSession, filteredEvalsNatReferenceSession]);




  // Données consolidées par type (maths + français)
  const mockDataByType = useMemo(() => {
    // Fonction pour extraire le préfixe du texte pour la compréhension écrite
    const getTextPrefix = (typeLabel) => {
      if (typeLabel?.startsWith("Compréhension de l'écrit ")) {
        let textName = typeLabel.replace("Compréhension de l'écrit ", "").trim();
        // Retirer "Document " si présent (ex: "Document Sommeil" -> "Sommeil")
        if (textName.startsWith("Document ")) {
          textName = textName.replace("Document ", "").trim();
        }
        return textName;
      }
      return null;
    };

    // Fonction générique pour créer les données consolidées par type
    const createMockDataForType = (typeKey) => {
      if (!mergedSession) {
        return [];
      }
      const consolidatedData = mergedSession.reduce((acc, evalNat) => {
        const statsKeys = evalNat.stats?.[typeKey] || [];

        statsKeys.forEach((stat) => {
          const id = parseInt(Object.keys(stat)[0], 10);
          const value = Object.values(stat)[0];
          const question = evalNat.questions?.find((q) => q.id === id);

          // Pour la compréhension écrite, récupérer le nom du texte
          const textPrefix = getTextPrefix(question?.type_label);

          let existing = acc.find((item) => item.id === id);
          if (!existing) {
            existing = {
              id: id,
              question_num: question?.question_num,
              text_name: textPrefix || null, // Nom du texte (Arachné, Sommeil, etc.)
              name: question?.nom_label || "Nom inconnu",
              results: {},
            };
            acc.push(existing);
          }
          existing.results[evalNat.session] = Math.round(value * 100);
        });

        return acc;
      }, []);

      return consolidatedData;
    };

    return {
      Automatismes: createMockDataForType('Automatismes'),
      Problèmes: createMockDataForType('Problèmes'),
      Lexique: createMockDataForType('Lexique'),
      ComprehensionEcrite: createMockDataForType('ComprehensionEcrite'),
      CompetencesLangagieres: createMockDataForType('CompetencesLangagieres'),
    };
  }, [mergedSession]);


  const handleTestSpecifiqueClick = (item) => {
    setTestSpecifique(item);
  };

 
const handleChangeNiveau = (item) => {
  setNiveau(item)
}

// Définir les boutons selon matière et niveau
const getTestButtons = (matiere, niveauParam) => {
  const buttons = [];
  const niv = niveauParam[0]; // "6eme" -> "6"

  if (matiere === 'maths') {
    buttons.push({ key: 'Automatismes', label: 'Automatismes' });
    if (niv === '6' || niv === '4') {
      buttons.push({ key: 'Problèmes', label: 'Résolution de problèmes' });
    }
  } else if (matiere === 'français') {
    switch(niv) {
      case '6':
        buttons.push({ key: 'Lexique', label: 'Lexique' });
        buttons.push({ key: 'ComprehensionEcrite', label: 'Compréhension écrite' });
        break;
      case '5':
        buttons.push({ key: 'CompetencesLangagieres', label: 'Compétences langagières' });
        break;
      case '4':
        buttons.push({ key: 'Lexique', label: 'Lexique' });
        buttons.push({ key: 'ComprehensionEcrite', label: 'Compréhension écrite' });
        break;
      default:
        buttons.push({ key: 'Lexique', label: 'Lexique' });
        buttons.push({ key: 'ComprehensionEcrite', label: 'Compréhension écrite' });
        break;
    }
  }

  return buttons;
}

const testButtons = getTestButtons(selectedMatiere, niveau);

// Réinitialiser testSpecifique quand la matière ou le niveau change
useEffect(() => {
  const buttons = getTestButtons(selectedMatiere, niveau);
  if (buttons.length > 0 && !buttons.find(b => b.key === testSpecifique)) {
    setTestSpecifique(buttons[0].key);
  }
}, [selectedMatiere, niveau, testSpecifique]);
  

  useEffect(() => {
    const mockData = mockDataByType[testSpecifique] || [];
    if (mockData.length > 0) {
      const processedData = mockData.map((item) => ({
        ...item,
        referenceResult: item.results[referenceYear],
        comparisonResult: item.results[comparisonYear],
        difference: item.results[referenceYear] - item.results[comparisonYear],
      }));
      setData(processedData);
    } else {
      setData([]);
    }
  }, [referenceYear, comparisonYear, mockDataByType, testSpecifique]);


  const availableYears = useMemo(() => {
    // Récupère les données du premier type disponible pour avoir les années
    const currentData = mockDataByType[testSpecifique] || [];
    if (currentData.length === 0) {
      return [];
    }

    const firstItemResults = currentData[0]?.results || {};

    return Object.keys(firstItemResults).map((year) => parseInt(year, 10));
  }, [mockDataByType, testSpecifique]);
// Utiliser un effet pour définir/réinitialiser comparisonYear
useEffect(() => {
  if (availableYears.length > 0) {
    // Si comparisonYear n'existe pas ou n'est plus dans les années disponibles, réinitialiser
    if (!comparisonYear || !availableYears.includes(comparisonYear)) {
      setComparisonYear(availableYears[0]); // Définit la première année comme valeur par défaut
    }
  }
}, [availableYears, comparisonYear, selectedMatiere, testSpecifique]); // Réagit aussi aux changements de matière et testSpecifique

if(isLoadingEvalNat){
  return <Loading loadingText={"Chargement des évaluations nationales..."}/>
}

  return (
    <div className="w-full mt-5 mb-5 flex justify-center">
      <div className="w-fit bg-white rounded-3xl shadow-2xl overflow-hidden">
        {/* Header avec gradient et blobs animés */}
        <div className="relative overflow-hidden bg-gradient-to-r from-blue-600 via-cyan-500 to-sky-400 px-4 sm:px-6 md:px-8 xl:px-5 2xl:px-6 3xl:px-8 py-6 sm:py-8 md:py-10 xl:py-6 2xl:py-8 3xl:py-10">
          {/* Blobs animés */}
          <svg className="absolute top-0 left-0 w-full h-full opacity-30" viewBox="0 0 200 200">
            <circle cx="20" cy="20" r="40" fill="#67e8f9">
              <animate attributeName="cx" values="20;60;20" dur="8s" repeatCount="indefinite" />
              <animate attributeName="cy" values="20;50;20" dur="6s" repeatCount="indefinite" />
            </circle>
            <circle cx="180" cy="80" r="30" fill="#a5f3fc">
              <animate attributeName="cx" values="180;140;180" dur="7s" repeatCount="indefinite" />
              <animate attributeName="cy" values="80;120;80" dur="9s" repeatCount="indefinite" />
            </circle>
            <circle cx="100" cy="150" r="35" fill="#22d3ee">
              <animate attributeName="cx" values="100;130;100" dur="10s" repeatCount="indefinite" />
              <animate attributeName="cy" values="150;170;150" dur="5s" repeatCount="indefinite" />
            </circle>
          </svg>

          <div className="relative z-10 text-center">
            <div className="flex justify-center mb-3 sm:mb-4 xl:mb-3 2xl:mb-3.5 3xl:mb-4">
              <div className="p-3 sm:p-4 xl:p-2.5 2xl:p-3 3xl:p-4 bg-white/20 backdrop-blur-sm rounded-2xl">
                <TrendingUp className="text-white w-8 h-8 sm:w-10 sm:h-10 xl:w-7 xl:h-7 2xl:w-8 2xl:h-8 3xl:w-10 3xl:h-10" size={40} />
              </div>
            </div>
            <h1 className="text-xl sm:text-2xl md:text-3xl xl:text-xl 2xl:text-2xl 3xl:text-3xl font-bold text-white mb-1.5 sm:mb-2 xl:mb-1.5 2xl:mb-1.5 3xl:mb-2">
              Évolution des Évaluations Nationales
            </h1>
            <p className="text-cyan-100 text-sm sm:text-base md:text-lg xl:text-sm 2xl:text-base 3xl:text-lg">
              Analyse comparative des performances de l'établissement
            </p>
          </div>
        </div>

        {/* Contenu principal */}
        <div className="p-4 sm:p-6 md:p-8 xl:p-5 2xl:p-6 3xl:p-8">
          {/* Sélection du niveau et matière */}
          <div className="mb-4 sm:mb-6 xl:mb-4 2xl:mb-5 3xl:mb-6 p-3 sm:p-4 md:p-5 xl:p-3.5 2xl:p-4 3xl:p-5 bg-gradient-to-r from-blue-50 to-cyan-50 border border-blue-200/50 rounded-2xl">
            <div className="flex flex-col lg:flex-row items-start lg:items-center gap-4 sm:gap-5 md:gap-6 xl:gap-4 2xl:gap-5 3xl:gap-6">
              {/* Sélecteur de niveau */}
              <div className="flex items-center gap-4 xl:gap-3 2xl:gap-3.5 3xl:gap-4">
                <label className="text-sm xl:text-xs 2xl:text-xs 3xl:text-sm font-semibold text-slate-700 flex items-center gap-2 xl:gap-1.5 2xl:gap-1.5 3xl:gap-2">
                  <span className="w-2 h-2 bg-gradient-to-r from-blue-500 to-cyan-500 rounded-full"></span>
                  Niveau scolaire
                </label>
                <div className="relative">
                  <select
                    className="appearance-none bg-white/80 border border-slate-200 text-slate-800 text-sm xl:text-xs 2xl:text-xs 3xl:text-sm
                    rounded-xl focus:ring-2 focus:ring-cyan-500/50 focus:border-cyan-300 block pl-4 xl:pl-3 2xl:pl-3.5 3xl:pl-4 pr-10 xl:pr-8 2xl:pr-9 3xl:pr-10 py-2.5 xl:py-2 2xl:py-2 3xl:py-2.5
                    transition-all duration-200 hover:shadow-md cursor-pointer font-medium"
                    value={niveau}
                    onChange={(e) => handleChangeNiveau(e.target.value)}
                  >
                    <option value="6eme">6ème</option>
                     <option value="5eme">5ème</option>
                    <option value="4eme">4ème</option>
                  </select>
                  <ChevronDown className="absolute right-3 top-1/2 transform -translate-y-1/2 text-slate-400 w-4 h-4 pointer-events-none" />
                </div>
              </div>

              {/* Séparateur vertical */}
              <div className="hidden lg:block w-px h-10 bg-slate-300"></div>

              {/* Switch de matière */}
              <div className="flex flex-col sm:flex-row sm:items-center gap-2 sm:gap-4 xl:gap-3 2xl:gap-3.5 3xl:gap-4">
                <label className="text-sm xl:text-xs 2xl:text-xs 3xl:text-sm font-semibold text-slate-700 flex items-center gap-2">
                  <span className="w-2 h-2 bg-gradient-to-r from-cyan-500 to-blue-500 rounded-full"></span>
                  Matière
                </label>
                <div className="flex bg-white/80 rounded-xl p-1 border border-slate-200 shadow-sm">
                  <button
                    onClick={() => setSelectedMatiere('maths')}
                    className={`flex items-center gap-2 xl:gap-1.5 2xl:gap-2 3xl:gap-2 px-4 xl:px-3 2xl:px-3.5 3xl:px-4 py-2 xl:py-1.5 2xl:py-1.5 3xl:py-2 rounded-lg text-sm xl:text-xs 2xl:text-xs 3xl:text-sm font-semibold transition-all duration-200 ${
                      selectedMatiere === 'maths'
                        ? 'bg-gradient-to-r from-blue-500 to-cyan-500 text-white shadow-lg shadow-cyan-500/30'
                        : 'text-slate-600 hover:text-slate-900 hover:bg-slate-100'
                    }`}
                  >
                    <Calculator size={16} className="xl:w-3.5 xl:h-3.5 2xl:w-[15px] 2xl:h-[15px] 3xl:w-4 3xl:h-4" />
                    Mathématiques
                  </button>
                  <button
                    onClick={() => setSelectedMatiere('français')}
                    className={`flex items-center gap-2 xl:gap-1.5 2xl:gap-2 3xl:gap-2 px-4 xl:px-3 2xl:px-3.5 3xl:px-4 py-2 xl:py-1.5 2xl:py-1.5 3xl:py-2 rounded-lg text-sm xl:text-xs 2xl:text-xs 3xl:text-sm font-semibold transition-all duration-200 ${
                      selectedMatiere === 'français'
                        ? 'bg-gradient-to-r from-blue-500 to-cyan-500 text-white shadow-lg shadow-cyan-500/30'
                        : 'text-slate-600 hover:text-slate-900 hover:bg-slate-100'
                    }`}
                  >
                    <BookOpen size={16} className="xl:w-3.5 xl:h-3.5 2xl:w-[15px] 2xl:h-[15px] 3xl:w-4 3xl:h-4" />
                    Français
                  </button>
                </div>
              </div>
            </div>
          </div>

          {/* Alerte classes sans résultats */}
          {(isClasseWitouthResult && isClasseWitouthResult.length > 0) ? (
            <div className="relative group mb-4 sm:mb-6 xl:mb-4 2xl:mb-5 3xl:mb-6">
              <div className="absolute -inset-0.5 bg-gradient-to-r from-amber-300 to-orange-300 rounded-2xl blur opacity-30" />
              <div className="relative bg-gradient-to-r from-amber-50 to-orange-50 border border-amber-200/50 rounded-2xl p-4 sm:p-5 md:p-6 xl:p-4 2xl:p-5 3xl:p-6">
                <div className="flex items-start gap-3 sm:gap-4 xl:gap-3 2xl:gap-3.5 3xl:gap-4">
                  <div className="p-2 sm:p-3 xl:p-2 2xl:p-2.5 3xl:p-3 bg-amber-100 rounded-xl shrink-0">
                    <AlertTriangle size={24} className="text-amber-600 w-5 h-5 sm:w-6 sm:h-6 xl:w-5 xl:h-5 2xl:w-[22px] 2xl:h-[22px] 3xl:w-6 3xl:h-6" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <h3 className="text-base sm:text-lg xl:text-base 2xl:text-base 3xl:text-lg font-bold text-amber-800 mb-1.5 sm:mb-2 xl:mb-1.5 2xl:mb-1.5 3xl:mb-2">
                      Importation des résultats nécessaire
                    </h3>
                    <p className="text-xs sm:text-sm xl:text-xs 2xl:text-xs 3xl:text-sm text-amber-700 mb-3 sm:mb-4 xl:mb-3 2xl:mb-3.5 3xl:mb-4">
                      Les résultats des évaluations nationales en <strong>{selectedMatiere}</strong> pour l'année en cours doivent être importés.
                    </p>
                    <div className="bg-white/60 backdrop-blur-sm rounded-xl p-3 sm:p-4 xl:p-3 2xl:p-3.5 3xl:p-4 mb-3 sm:mb-4 xl:mb-3 2xl:mb-3.5 3xl:mb-4">
                      <p className="text-[10px] sm:text-xs xl:text-[10px] 2xl:text-[11px] 3xl:text-xs text-amber-600 uppercase tracking-wide font-semibold mb-2 sm:mb-3 xl:mb-2 2xl:mb-2.5 3xl:mb-3">Classes sans résultats :</p>
                      <div className="flex flex-wrap gap-2 xl:gap-1.5 2xl:gap-2 3xl:gap-2">
                        {isClasseWitouthResult.map((classe, index) => (
                          <span key={index} className="inline-flex items-center px-3 xl:px-2 2xl:px-2.5 3xl:px-3 py-1 xl:py-0.5 2xl:py-0.5 3xl:py-1 bg-amber-100 text-amber-700 rounded-lg text-sm xl:text-xs 2xl:text-xs 3xl:text-sm font-medium">
                            {classe}
                          </span>
                        ))}
                      </div>
                    </div>
                    <p className="text-sm xl:text-xs 2xl:text-xs 3xl:text-sm text-amber-700 flex items-center gap-2">
                      <span className="w-1.5 h-1.5 bg-amber-500 rounded-full"></span>
                      Dirigez-vous vers l'accueil pour importer les données.
                    </p>
                  </div>
                </div>
              </div>
            </div>
          ) : (
            <>
              {/* Onglets de navigation */}
              <div className="flex flex-col items-start gap-3 sm:gap-5 xl:gap-3.5 2xl:gap-4 3xl:gap-5 justify-between mb-4 sm:mb-6 xl:mb-4 2xl:mb-5 3xl:mb-6">
                <div className="flex overflow-x-auto w-full sm:w-auto bg-white/80 rounded-xl p-1 border border-slate-200 shadow-sm">
                  {testButtons.map((button) => (
                    <button
                      key={button.key}
                      onClick={() => handleTestSpecifiqueClick(button.key)}
                      className={`px-3 sm:px-6 xl:px-4 2xl:px-5 3xl:px-6 py-2 sm:py-3 xl:py-2 2xl:py-2.5 3xl:py-3 rounded-lg font-semibold text-xs sm:text-sm xl:text-xs 2xl:text-xs 3xl:text-sm whitespace-nowrap transition-all duration-300 ${
                        testSpecifique === button.key
                          ? "bg-gradient-to-r from-blue-500 to-cyan-500 text-white shadow-lg shadow-cyan-500/30"
                          : "text-slate-500 hover:text-slate-700 hover:bg-slate-100"
                      }`}
                    >
                      {button.label}
                    </button>
                  ))}
                </div>

                <div className="flex flex-wrap items-center gap-2 sm:gap-3 xl:gap-2 2xl:gap-2.5 3xl:gap-3">
                  <button
                    onClick={() => setShowImportModal(true)}
                    className="inline-flex items-center gap-1.5 sm:gap-2 xl:gap-1.5 2xl:gap-2 3xl:gap-2 px-3 sm:px-4 xl:px-3 2xl:px-3.5 3xl:px-4 py-2 sm:py-2.5 xl:py-2 2xl:py-2 3xl:py-2.5 bg-gradient-to-r from-emerald-500 to-green-600 text-white text-xs sm:text-sm xl:text-xs 2xl:text-xs 3xl:text-sm font-semibold
                      rounded-xl shadow-lg shadow-emerald-500/25 hover:shadow-xl hover:shadow-emerald-500/30 hover:scale-[1.02]
                      transition-all duration-200"
                  >
                    <History size={18} className="w-4 h-4 sm:w-[18px] sm:h-[18px] xl:w-4 xl:h-4 2xl:w-[17px] 2xl:h-[17px] 3xl:w-[18px] 3xl:h-[18px]" />
                    <span className="hidden sm:inline">Importer année antérieure</span>
                    <span className="sm:hidden">Importer</span>
                  </button>
                  <DeleteAnteriorEvalNat
                    anteriorEvaluations={EvalsNatGroupe}
                    handleReload={handleReload}
                  />
                  <ExportEvolution
                    mockDataByType={mockDataByType}
                    testSpecifique={testSpecifique}
                    referenceYear={referenceYear}
                    comparisonYear={comparisonYear}
                    niveau={niveau}
                  />
                </div>
              </div>

              {/* Tableau des résultats */}
              <div className="overflow-hidden rounded-xl sm:rounded-2xl border border-slate-200 shadow-lg">
                <div className="overflow-auto max-h-[60vh] sm:max-h-[50vh]">
                  <table className="min-w-full">
                    <thead className="sticky top-0 z-10 bg-gradient-to-r from-blue-600 via-cyan-500 to-sky-400 text-white">
                      <tr>
                        <th className="px-2 sm:px-5 xl:px-3.5 2xl:px-4 3xl:px-5 py-2.5 sm:py-4 xl:py-3 2xl:py-3.5 3xl:py-4 text-left text-[10px] sm:text-xs xl:text-[10px] 2xl:text-[11px] 3xl:text-xs font-semibold uppercase tracking-wider">
                          <div className="flex items-center gap-2">
                            <span className="hidden sm:flex w-7 xl:w-5 2xl:w-6 3xl:w-7 h-7 xl:h-5 2xl:h-6 3xl:h-7 bg-white/20 backdrop-blur-sm rounded-lg items-center justify-center text-xs xl:text-[10px] 2xl:text-[11px] 3xl:text-xs">#</span>
                            Question
                          </div>
                        </th>
                        <th className="px-2 sm:px-5 xl:px-3.5 2xl:px-4 3xl:px-5 py-2.5 sm:py-4 xl:py-3 2xl:py-3.5 3xl:py-4 text-left text-[10px] sm:text-xs xl:text-[10px] 2xl:text-[11px] 3xl:text-xs font-semibold uppercase tracking-wider">
                          Savoir-Faire / Problème
                        </th>
                        <th className="px-2 sm:px-5 xl:px-3.5 2xl:px-4 3xl:px-5 py-2.5 sm:py-4 xl:py-3 2xl:py-3.5 3xl:py-4 text-left text-[10px] sm:text-xs xl:text-[10px] 2xl:text-[11px] 3xl:text-xs font-semibold uppercase tracking-wider">
                          <div className="flex items-center gap-2">
                            <span>% Réussite</span>
                            <select
                              className="ml-2 bg-white/20 backdrop-blur-sm text-white border border-white/30 rounded-lg px-3 py-1.5
                              text-xs xl:text-[10px] 2xl:text-[11px] 3xl:text-xs font-medium focus:outline-none focus:ring-2 focus:ring-white/50 cursor-pointer"
                              value={comparisonYear || ""}
                              onLoad={(e) => setComparisonYear(parseInt(e.target.value))}
                              onChange={(e) => setComparisonYear(parseInt(e.target.value))}
                            >
                              {availableYears.map((year) => (
                                <option key={year} value={year} className="text-slate-800">
                                  {year}
                                </option>
                              ))}
                            </select>
                          </div>
                        </th>
                        <th className="px-2 sm:px-5 xl:px-3.5 2xl:px-4 3xl:px-5 py-2.5 sm:py-4 xl:py-3 2xl:py-3.5 3xl:py-4 text-left text-[10px] sm:text-xs xl:text-[10px] 2xl:text-[11px] 3xl:text-xs font-semibold uppercase tracking-wider">
                          % Réussite {referenceYear}
                        </th>
                        <th className="px-2 sm:px-5 xl:px-3.5 2xl:px-4 3xl:px-5 py-2.5 sm:py-4 xl:py-3 2xl:py-3.5 3xl:py-4 text-left text-[10px] sm:text-xs xl:text-[10px] 2xl:text-[11px] 3xl:text-xs font-semibold uppercase tracking-wider">
                          <div className="flex items-center gap-2">
                            <TrendingUp size={14} />
                            Écart
                          </div>
                        </th>
                      </tr>
                    </thead>
                    <tbody className="bg-white divide-y divide-slate-100">
                      {data.map((item, index) => (
                        <tr key={item.id} className="hover:bg-cyan-50/50 transition-colors duration-200">
                          <td className="px-2 sm:px-5 xl:px-3.5 2xl:px-4 3xl:px-5 py-2.5 sm:py-4 xl:py-3 2xl:py-3.5 3xl:py-4 whitespace-nowrap">
                            <div className="flex flex-col items-start gap-1">
                              {item.text_name && (
                                <span className="text-[10px] xl:text-[8px] 2xl:text-[9px] 3xl:text-[10px] font-medium text-cyan-600 bg-cyan-50 px-2 xl:px-1.5 2xl:px-1.5 3xl:px-2 py-0.5 rounded-md">
                                  {item.text_name}
                                </span>
                              )}
                              <span className="w-6 sm:w-8 xl:w-6 2xl:w-7 3xl:w-8 h-6 sm:h-8 xl:h-6 2xl:h-7 3xl:h-8 bg-gradient-to-br from-blue-100 to-cyan-100 rounded-lg sm:rounded-xl flex items-center justify-center text-xs sm:text-sm xl:text-xs 2xl:text-xs 3xl:text-sm font-bold text-blue-600">
                                {item.question_num}
                              </span>
                            </div>
                          </td>
                          <td className="px-2 sm:px-5 xl:px-3.5 2xl:px-4 3xl:px-5 py-2.5 sm:py-4 xl:py-3 2xl:py-3.5 3xl:py-4">
                            <span className="text-xs sm:text-sm xl:text-xs 2xl:text-xs 3xl:text-sm text-slate-700">
                              <span className="sm:hidden">{item.name.split(' ')[0]}...</span>
                              <span className="hidden sm:inline">{item.name}</span>
                            </span>
                          </td>
                          <td className="px-2 sm:px-5 xl:px-3.5 2xl:px-4 3xl:px-5 py-2.5 sm:py-4 xl:py-3 2xl:py-3.5 3xl:py-4">
                            <div className="flex items-center gap-3">
                              <div className="w-14 sm:w-24 xl:w-16 2xl:w-20 3xl:w-24 bg-white/50 backdrop-blur-sm rounded-full h-2 sm:h-2.5 xl:h-2 2xl:h-2 3xl:h-2.5 shadow-inner border border-slate-200/50">
                                <div
                                  className="h-2 sm:h-2.5 xl:h-2 2xl:h-2 3xl:h-2.5 rounded-full transition-all duration-500 shadow-sm"
                                  style={{
                                    width: `${item.comparisonResult}%`,
                                    background: item.comparisonResult < 40
                                      ? 'linear-gradient(to right, #f87171, #ef4444, #dc2626)'
                                      : item.comparisonResult < 70
                                        ? 'linear-gradient(to right, #fbbf24, #f59e0b, #d97706)'
                                        : 'linear-gradient(to right, #34d399, #10b981, #059669)'
                                  }}
                                ></div>
                              </div>
                              <span className={`text-xs sm:text-sm xl:text-xs 2xl:text-xs 3xl:text-sm font-semibold w-10 sm:w-12 xl:w-10 2xl:w-11 3xl:w-12 ${
                                item.comparisonResult < 40 ? 'text-red-600' : item.comparisonResult < 70 ? 'text-amber-600' : 'text-emerald-600'
                              }`}>{item.comparisonResult}%</span>
                            </div>
                          </td>
                          <td className="px-2 sm:px-5 xl:px-3.5 2xl:px-4 3xl:px-5 py-2.5 sm:py-4 xl:py-3 2xl:py-3.5 3xl:py-4">
                            <div className="flex items-center gap-3">
                              <div className="w-14 sm:w-24 xl:w-16 2xl:w-20 3xl:w-24 bg-white/50 backdrop-blur-sm rounded-full h-2 sm:h-2.5 xl:h-2 2xl:h-2 3xl:h-2.5 shadow-inner border border-slate-200/50">
                                <div
                                  className="h-2 sm:h-2.5 xl:h-2 2xl:h-2 3xl:h-2.5 rounded-full transition-all duration-500 shadow-sm"
                                  style={{
                                    width: `${item.referenceResult}%`,
                                    background: item.referenceResult < 40
                                      ? 'linear-gradient(to right, #f87171, #ef4444, #dc2626)'
                                      : item.referenceResult < 70
                                        ? 'linear-gradient(to right, #fbbf24, #f59e0b, #d97706)'
                                        : 'linear-gradient(to right, #34d399, #10b981, #059669)'
                                  }}
                                ></div>
                              </div>
                              <span className={`text-xs sm:text-sm xl:text-xs 2xl:text-xs 3xl:text-sm font-semibold w-10 sm:w-12 xl:w-10 2xl:w-11 3xl:w-12 ${
                                item.referenceResult < 40 ? 'text-red-600' : item.referenceResult < 70 ? 'text-amber-600' : 'text-emerald-600'
                              }`}>{item.referenceResult}%</span>
                            </div>
                          </td>
                          <td className="px-2 sm:px-5 xl:px-3.5 2xl:px-4 3xl:px-5 py-2.5 sm:py-4 xl:py-3 2xl:py-3.5 3xl:py-4">
                            <span className={`inline-flex items-center gap-1 px-3 xl:px-2 2xl:px-2.5 3xl:px-3 py-1.5 xl:py-1 2xl:py-1 3xl:py-1.5 rounded-xl text-xs xl:text-[10px] 2xl:text-[11px] 3xl:text-xs font-bold ${
                              item.difference > 0
                                ? "bg-emerald-100 text-emerald-700"
                                : item.difference < 0
                                  ? "bg-red-100 text-red-700"
                                  : "bg-slate-100 text-slate-600"
                            }`}>
                              {item.difference > 0 && <ArrowUp size={12} />}
                              {item.difference < 0 && <ArrowDown size={12} />}
                              {item.difference === 0 && <Minus size={12} />}
                              {item.difference > 0 ? `+${item.difference}%` : `${item.difference}%`}
                            </span>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            </>
          )}
        </div>
      </div>

      {/* Modal d'importation */}
      {showImportModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
          {/* Backdrop */}
          <div
            className="absolute inset-0 bg-slate-900/60 backdrop-blur-sm"
            onClick={() => setShowImportModal(false)}
          />

          {/* Modal content */}
          <div className="relative max-w-2xl w-full max-h-[90vh] overflow-auto">
            <ImportEvolution
              availableYears={availableYears}
              EvalsNatGroupe={EvalsNatGroupe}
              etablissement={etablissementData}
              niveau={niveau}
              handleReload={handleReload}
              selectedMatiere={selectedMatiere}
              onClose={() => setShowImportModal(false)}
            />
          </div>
        </div>
      )}
    </div>
  );
};


export default Evolution;